import React from 'react';
import { Link } from 'react-router-dom';
import { campaignService } from '../services/campaignService';
import { formatCompactNumber, formatPercent, formatDateTime } from '../utils/format';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import Dropdown from '../components/ui/Dropdown';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EventBus from '../utils/EventBus';
import styles from './Campaigns.module.css';

const FILTERS = ['all', 'draft', 'scheduled', 'sending', 'completed'];
const PAGE_SIZE = 20;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function currentMonthYear() {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

// A handful of years back is plenty — this platform hasn't been live long
// enough for a campaign to exist further back than that.
function yearOptions() {
  const { year } = currentMonthYear();
  return [year, year - 1, year - 2];
}

// scheduled_at only reflects a future send picked at creation time — once a
// campaign actually starts, launched_at is the real signal (set for both
// scheduled sends when their time arrives and immediate "send now" launches).
function scheduleLabel(c) {
  if (c.launched_at) return 'Sent ' + formatDateTime(c.launched_at);
  if (c.scheduled_at) return 'Scheduled for ' + formatDateTime(c.scheduled_at);
  return 'Draft';
}

class CampaignRowSkeleton extends React.Component {
  render() {
    return (
      <tr className={styles.skRow}>
        <td><Skeleton variant="text" width={180} height={13} /></td>
        <td><Skeleton variant="text" width={60} height={13} /></td>
        <td><Skeleton variant="text" width={50} height={13} /></td>
        <td><Skeleton variant="text" width={110} height={13} /></td>
        <td><Skeleton variant="text" width={130} height={13} /></td>
        <td><Skeleton variant="text" width={60} height={13} /></td>
        <td><Skeleton variant="text" width={40} height={13} /></td>
        <td><Skeleton variant="text" width={40} height={13} /></td>
        <td><Skeleton variant="text" width={50} height={13} /></td>
        <td><Skeleton variant="text" width={50} height={13} /></td>
        <td><Skeleton variant="rect" width={52} height={24} style={{ borderRadius: 6 }} /></td>
      </tr>
    );
  }
}

class Campaigns extends React.Component {
  constructor(props) {
    super(props);
    const { month, year } = currentMonthYear();
    this.state = {
      loading: true, filter: 'all', campaigns: [], total: 0, statusCounts: {},
      page: 1, month, year,
      confirmDelete: null, confirmCancel: null, acting: false,
    };
    this.load = this.load.bind(this);
    this.act = this.act.bind(this);
  }

  componentDidMount() {
    this.load();
    EventBus.on('toast', this._maybeReload = (t) => {
      if (t?.message?.includes('launched') || t?.message === 'Draft saved.') this.load();
    });
  }

  componentWillUnmount() {
    EventBus.off('toast', this._maybeReload);
  }

  load() {
    const { filter, page, month, year } = this.state;
    this.setState({ loading: true });
    campaignService.getAll({
      page,
      limit: PAGE_SIZE,
      status: filter === 'all' ? undefined : filter,
      month,
      year,
    })
      .then(({ data, meta }) => this.setState({
        loading: false, campaigns: data, total: meta.total, statusCounts: meta.statusCounts || {},
      }))
      .catch(() => this.setState({ loading: false }));
  }

  setFilter(filter) {
    this.setState({ filter, page: 1 }, this.load);
  }

  setPage(page) {
    this.setState({ page }, this.load);
  }

  setMonth(month) {
    this.setState({ month, page: 1 }, this.load);
  }

  setYear(year) {
    this.setState({ year, page: 1 }, this.load);
  }

  backToCurrentMonth() {
    this.setState({ ...currentMonthYear(), page: 1 }, this.load);
  }

  act(promise, successMessage) {
    this.setState({ acting: true });
    promise
      .then(() => {
        this.setState({ acting: false, confirmDelete: null, confirmCancel: null });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  menuItems(c) {
    const items = [];
    if (['draft', 'scheduled', 'paused'].includes(c.status)) {
      items.push({ label: 'Edit', onClick: () => EventBus.emit('openWizard', { campaign: c }) });
    }
    items.push({
      label: 'Duplicate',
      onClick: () => this.act(campaignService.duplicate(c.id), 'Campaign duplicated.'),
    });
    if (['scheduled', 'sending'].includes(c.status)) {
      items.push({ label: 'Pause', onClick: () => this.act(campaignService.pause(c.id), 'Campaign paused.') });
    }
    if (c.status === 'paused') {
      items.push({ label: 'Resume', onClick: () => this.act(campaignService.resume(c.id), 'Campaign resumed.') });
    }
    if (!['completed', 'cancelled'].includes(c.status)) {
      items.push({ divider: true }, { label: 'Cancel', danger: true, onClick: () => this.setState({ confirmCancel: c }) });
    }
    items.push({ label: 'Delete', danger: true, onClick: () => this.setState({ confirmDelete: c }) });
    return items;
  }

  render() {
    const { loading, filter, campaigns, total, statusCounts, page, month, year, confirmDelete, confirmCancel, acting } = this.state;
    const rows = campaigns;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const allCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
    const isCurrentMonth = month === currentMonthYear().month && year === currentMonthYear().year;

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Campaigns</div>
            <div className={styles.pageSub}>All your messaging campaigns · push · in-app · WhatsApp · email</div>
          </div>
          <button
            className={styles.newBtn}
            onClick={() => EventBus.emit('openWizard', {})}
          >
            + New Campaign
          </button>
        </div>

        <div className={styles.dateFilterRow}>
          <Select
            size="sm"
            style={{ width: 130 }}
            value={month}
            options={MONTH_NAMES.map((name, i) => ({ value: i + 1, label: name }))}
            onChange={(val) => this.setMonth(Number(val))}
          />
          <Select
            size="sm"
            style={{ width: 90 }}
            value={year}
            options={yearOptions().map((y) => ({ value: y, label: String(y) }))}
            onChange={(val) => this.setYear(Number(val))}
          />
          {!isCurrentMonth && (
            <button className={styles.clearDateBtn} onClick={() => this.backToCurrentMonth()}>
              Back to this month
            </button>
          )}
        </div>

        <div className={styles.tabs}>
          {FILTERS.map((f) => (
            <button
              key={f}
              className={[styles.tab, filter === f ? styles.tabOn : ''].join(' ')}
              onClick={() => this.setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span className={styles.tabCount}>
                {f === 'all' ? allCount : (statusCounts[f] || 0)}
              </span>
            </button>
          ))}
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Campaign</th>
                <th className={styles.th}>Channel</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th}>Created</th>
                <th className={styles.th}>Schedule</th>
                <th className={styles.th}>Sent</th>
                <th className={styles.th}>Delivered</th>
                <th className={styles.th}>Open %</th>
                <th className={styles.th}>CTR %</th>
                <th className={styles.th}>Converted</th>
                <th className={styles.th}>Link Clicks</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [0, 1, 2, 3, 4].map((i) => <CampaignRowSkeleton key={i} />)
                : rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={11}>
                        <EmptyState
                          icon="📢"
                          title="No campaigns"
                          description={isCurrentMonth && filter === 'all'
                            ? 'Create your first campaign to get started.'
                            : 'No campaigns match this month/filter — try another month or clear the filter.'}
                        />
                      </td>
                    </tr>
                  )
                  : rows.map((c) => {
                      const openPct = c.sent_count ? (c.opened_count / c.sent_count) * 100 : null;
                      const ctrPct = c.sent_count ? (c.clicked_count / c.sent_count) * 100 : null;
                      return (
                        <tr key={c.id} className={styles.row}>
                          <td className={styles.tdName}>
                            <Link to={'/campaigns/' + c.id} style={{ color: 'inherit', textDecoration: 'none' }}>{c.name}</Link>
                          </td>
                          <td className={styles.td}>{(c.contents || []).map((ct) => ct.channel).join(', ') || '—'}</td>
                          <td className={styles.td}><Badge status={c.status} dot>{c.status}</Badge></td>
                          <td className={styles.td}>
                            {formatDateTime(c.created_on)}
                            <div className={styles.tdSub}>by {c.created_by_name || 'Unknown'}</div>
                          </td>
                          <td className={styles.td}>{scheduleLabel(c)}</td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.sent_count)}</td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.delivered_count)}</td>
                          <td className={styles.tdNum}>{formatPercent(openPct)}</td>
                          <td className={styles.tdNum}>{formatPercent(ctrPct)}</td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.converted_count)}</td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.link_clicks)}</td>
                          <td className={styles.tdAct}>
                            <Dropdown
                              align="right"
                              trigger={<button className={styles.actBtn}>⋯</button>}
                              items={this.menuItems(c)}
                            />
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>

        {!loading && rows.length > 0 && (
          <div className={styles.pager}>
            <button className={styles.pagerBtn} disabled={page <= 1} onClick={() => this.setPage(page - 1)}>‹ Prev</button>
            <span className={styles.pagerInfo}>Page {page} of {totalPages} · {formatCompactNumber(total)} campaigns</span>
            <button className={styles.pagerBtn} disabled={page >= totalPages} onClick={() => this.setPage(page + 1)}>Next ›</button>
          </div>
        )}

        <ConfirmDialog
          open={!!confirmDelete}
          title="Delete campaign?"
          message={confirmDelete ? `"${confirmDelete.name}" will be removed. This can't be undone.` : ''}
          confirmLabel="Delete"
          loading={acting}
          onConfirm={() => this.act(campaignService.remove(confirmDelete.id), 'Campaign deleted.')}
          onCancel={() => this.setState({ confirmDelete: null })}
        />

        <ConfirmDialog
          open={!!confirmCancel}
          title="Cancel campaign?"
          message={confirmCancel ? `"${confirmCancel.name}" will stop sending immediately. This can't be undone.` : ''}
          confirmLabel="Cancel campaign"
          loading={acting}
          onConfirm={() => this.act(campaignService.cancel(confirmCancel.id), 'Campaign cancelled.')}
          onCancel={() => this.setState({ confirmCancel: null })}
        />
      </>
    );
  }
}

export default Campaigns;
